<script setup>
// SMC two-tier nav: white secondary panel showing the active section's sub-menu,
// reusing Sidebar.vue's own `menuItems` (via sharedMenu). Sits between the dark
// rail and the main content (mounted in Dashboard.vue).
// Collapsible: a toggle slides it left (thin strip) to widen the page; a chevron
// re-opens it to change menu selection. State persisted in localStorage.
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { sharedMenuItems } from './sharedMenu';

const route = useRoute();

// Inbox is a single page — no second menu.
const EXCLUDE = ['Inbox'];

const collapsed = ref(localStorage.getItem('smc_secondary_collapsed') === '1');
const toggle = () => {
  collapsed.value = !collapsed.value;
  localStorage.setItem('smc_secondary_collapsed', collapsed.value ? '1' : '0');
};

const matchesRoute = (item, name) => {
  if (item?.to?.name === name) return true;
  if ((item?.activeOn || []).includes(name)) return true;
  return (item?.children || []).some(c => matchesRoute(c, name));
};

const section = computed(() => {
  const name = route.name || '';
  return (
    (sharedMenuItems.value || []).find(
      it => !EXCLUDE.includes(it.name) && it.children?.length && matchesRoute(it, name)
    ) || null
  );
});

const flatItems = computed(() => {
  if (!section.value) return [];
  const out = [];
  const walk = children => {
    for (const c of children || []) {
      if (c.to) {
        out.push({ type: 'link', label: c.label, to: c.to, activeOn: c.activeOn });
      } else if (c.children?.length) {
        out.push({ type: 'header', label: c.label });
        walk(c.children);
      }
    }
  };
  walk(section.value.children);
  return out;
});

const isActive = item =>
  route.name === item.to?.name || (item.activeOn || []).includes(route.name);
</script>

<template>
  <!-- Collapsed: thin strip with an expand (») button -->
  <aside
    v-if="section && flatItems.length && collapsed"
    class="flex flex-col items-center flex-shrink-0 w-9 h-full bg-white border-r border-n-weak pt-4"
  >
    <button
      type="button"
      title="Tampilkan menu"
      class="flex items-center justify-center size-8 rounded-lg bg-[#EAF1FE] text-[#1B57CE] shadow-sm ring-1 ring-[#1B57CE]/15 transition-colors hover:bg-[#D7E6FD]"
      @click="toggle"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6" /></svg>
    </button>
  </aside>

  <!-- Expanded: full panel with a collapse («) button in the header -->
  <aside
    v-else-if="section && flatItems.length"
    class="flex flex-col flex-shrink-0 w-[248px] h-full bg-white border-r border-n-weak"
  >
    <div class="flex items-center justify-between px-5 pt-5 pb-3">
      <h2 class="text-xl font-bold text-n-slate-12">{{ section.label }}</h2>
      <button
        type="button"
        title="Sembunyikan menu"
        class="flex items-center justify-center flex-shrink-0 size-8 rounded-lg bg-[#EAF1FE] text-[#1B57CE] shadow-sm ring-1 ring-[#1B57CE]/15 transition-colors hover:bg-[#D7E6FD]"
        @click="toggle"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>
    </div>
    <nav class="flex flex-col gap-0.5 px-3 pb-4 overflow-y-auto">
      <template v-for="(item, i) in flatItems" :key="i">
        <div
          v-if="item.type === 'header'"
          class="px-3 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-n-slate-10"
        >
          {{ item.label }}
        </div>
        <router-link
          v-else
          :to="item.to"
          class="flex items-center gap-2.5 px-3 h-9 rounded-lg text-sm font-medium transition-colors"
          :class="
            isActive(item)
              ? 'bg-[#EAF1FE] text-[#1B57CE]'
              : 'text-n-slate-12 hover:bg-n-alpha-1'
          "
        >
          <span
            class="size-1.5 rounded-full flex-shrink-0"
            :class="isActive(item) ? 'bg-[#1B57CE]' : 'bg-n-slate-6'"
          />
          {{ item.label }}
        </router-link>
      </template>
    </nav>
  </aside>
</template>
